import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown, Star, VolumeX, Volume2 } from "lucide-react";
import { useContactForm } from "@/contexts/ContactFormContext";
import { useTranslation } from "@/i18n/LanguageContext";

const ease = [0.16, 1, 0.3, 1] as const;

const ScrollHero = () => {
  const { openForm } = useContactForm();
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [muted, setMuted] = useState(true);
  const [textVisible, setTextVisible] = useState(false);
  const reducedMotion = useReducedMotion();

  const statCards = [
    { value: "5.0", label: t("hero.clientRating"), icon: true },
    { value: "100", label: t("hero.seoScore"), cyan: true },
    { value: "2/mo", label: t("hero.maxCapacity") },
  ];

  const clients = ["Hair by Ellie", "Stemit", "Zi-eM Hamali"];
  const headline = t("hero.headline");
  const subtext = t("hero.subtext");

  useEffect(() => {
    if (reducedMotion) { setTextVisible(true); return; }
    const timer = setTimeout(() => setTextVisible(true), 600);
    return () => clearTimeout(timer);
  }, [reducedMotion]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => {
      video.pause();
      if (video.duration && isFinite(video.duration)) video.currentTime = video.duration - 0.01;
    };
    const handleTimeUpdate = () => {
      if (video.duration && isFinite(video.duration) && video.currentTime >= video.duration - 0.05) {
        video.pause();
        video.currentTime = video.duration - 0.01;
      }
    };
    video.addEventListener("ended", handleEnded);
    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => { video.removeEventListener("ended", handleEnded); video.removeEventListener("timeupdate", handleTimeUpdate); };
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const next = !muted;
    setMuted(next);
    video.muted = next;
    if (!next) video.volume = 0.35;
  }, [muted]);

  useEffect(() => {
    if (reducedMotion) return;
    const container = videoContainerRef.current;
    const section = sectionRef.current;
    const video = videoRef.current;
    if (!container || !section) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const rect = section.getBoundingClientRect();
        if (rect.bottom > 0) {
          const scrolled = Math.max(0, -rect.top);
          container.style.transform = `translateY(${scrolled * 0.35}px)`;
        }
        if (video && !video.muted) {
          const visibility = Math.max(0, Math.min(1, rect.bottom / window.innerHeight));
          video.volume = 0.35 * visibility;
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [reducedMotion]);

  const scrollToNext = useCallback(() => {
    const section = sectionRef.current;
    if (!section) return;
    const next = section.nextElementSibling as HTMLElement;
    if (next) next.scrollIntoView({ behavior: "smooth" });
  }, []);

  const words = headline.split(" ");
  const instant = reducedMotion;
  const stagger = (baseDelay: number) => instant ? { duration: 0 } : { delay: baseDelay, duration: 0.5, ease };

  return (
    <section ref={sectionRef} id="hero-section" className="relative w-full overflow-hidden" style={{ minHeight: "100svh", background: "hsl(var(--background))" }}>
      <div ref={videoContainerRef} className="absolute inset-0" style={{ willChange: "transform" }}>
        {/* @ts-ignore */}
        <video ref={videoRef} autoPlay muted playsInline preload="auto" fetchpriority="high" src="/hero_zephr.mp4" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
      </div>

      {/* Gradient overlays */}
      <div className="absolute inset-0 z-[1] md:hidden" style={{ background: "linear-gradient(to bottom, rgba(10,10,15,0) 0%, rgba(10,10,15,0) 30%, rgba(10,10,15,0.25) 40%, rgba(10,10,15,0.65) 48%, rgba(10,10,15,0.9) 55%, rgba(10,10,15,1) 62%, rgba(10,10,15,1) 100%)" }} />
      <div className="absolute inset-0 z-[1] hidden md:block" style={{ background: "linear-gradient(to right, rgba(10,10,15,0.95) 0%, rgba(10,10,15,0.85) 25%, rgba(10,10,15,0.5) 45%, rgba(10,10,15,0) 65%)" }} />
      <div className="absolute inset-0 z-[1] hidden md:block pointer-events-none" style={{ background: "linear-gradient(to bottom, rgba(10,10,15,0) 70%, rgba(10,10,15,0.6) 85%, rgba(10,10,15,1) 100%)" }} />
      <div className="absolute inset-0 grain-overlay pointer-events-none z-[2]" />

      {/* Unmute button */}
      <button onClick={toggleMute} className="absolute z-[10] flex items-center justify-center" style={{ top: "calc(env(safe-area-inset-top, 16px) + 60px)", right: "16px", width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.1)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", border: "0.5px solid rgba(255,255,255,0.12)" }} aria-label={muted ? "Unmute" : "Mute"}>
        {muted ? <VolumeX className="w-4 h-4 text-foreground/70" /> : <Volume2 className="w-4 h-4 text-foreground/70" />}
      </button>

      {/* Text content */}
      <div className="relative z-[3] flex items-end md:items-center px-6 md:px-[5%]" style={{ minHeight: "100svh" }}>
        <div className="w-full max-w-7xl mx-auto pb-3 md:pb-0">
          <div className="max-w-lg lg:max-w-xl">
            <motion.p className="text-[13px] md:text-xs tracking-[0.2em] uppercase mb-2 font-medium text-primary" initial={{ opacity: 0, y: 10 }} animate={textVisible ? { opacity: 1, y: 0 } : {}} transition={stagger(0)}>
              {t("hero.label")}
            </motion.p>
            <h1 className="font-display font-extrabold text-foreground leading-[1.15] tracking-[-0.02em]" style={{ fontSize: "clamp(1.6rem, 1.8rem + 1vw, 3.2rem)" }}>
              {words.map((word, i) => (
                <motion.span key={i} className="inline-block mr-[0.3em]" initial={{ opacity: 0, x: -20 }} animate={textVisible ? { opacity: 1, x: 0 } : {}} transition={instant ? { duration: 0 } : { delay: 0.2 + i * 0.08, duration: 0.5, ease }} style={{ textShadow: "0 2px 20px rgba(0,0,0,0.5)" }}>
                  {word}
                </motion.span>
              ))}
            </h1>
            <motion.p className="mt-2 text-sm md:text-base text-muted-foreground max-w-[340px] md:max-w-md leading-relaxed" initial={{ opacity: 0, y: 16 }} animate={textVisible ? { opacity: 1, y: 0 } : {}} transition={stagger(0.5)}>
              {subtext}
            </motion.p>
            <motion.div className="mt-3 md:mt-4" initial={{ opacity: 0, y: 12 }} animate={textVisible ? { opacity: 1, y: 0 } : {}} transition={stagger(0.8)}>
              <button onClick={openForm} className="btn-shimmer px-7 py-3.5 min-h-[48px] rounded-full text-primary-foreground font-display font-bold text-[15px] tracking-wide hover:scale-[1.03] active:scale-[0.97]" style={{ transition: "transform 0.3s, box-shadow 0.3s", background: "linear-gradient(135deg, hsl(var(--primary)), hsl(191 100% 42%))", boxShadow: "0 0 20px hsl(191 100% 50% / 0.2)" }}>
                {t("hero.cta")}
              </button>
            </motion.div>
            <motion.p className="mt-1.5 md:mt-2 text-xs text-muted-foreground/50 tracking-wide" initial={{ opacity: 0 }} animate={textVisible ? { opacity: 1 } : {}} transition={stagger(1.0)}>
              {t("hero.ctaSubtext")}
            </motion.p>

            {/* Stat cards */}
            <div className="mt-3 md:mt-4 flex items-stretch gap-2">
              {statCards.map((card, i) => (
                <motion.div key={card.label} className="flex-1 rounded-xl px-3 py-2 md:py-2.5 text-center border border-primary/15" style={{ background: "rgba(255,255,255,0.08)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }} initial={{ opacity: 0, y: 12 }} animate={textVisible ? { opacity: 1, y: 0 } : {}} transition={instant ? { duration: 0 } : { delay: 1.0 + i * 0.08, duration: 0.4, ease }}>
                  <div className="flex items-center justify-center gap-1">
                    {card.icon && <Star className="w-3.5 h-3.5 text-accent fill-accent" />}
                    <span className={`text-base font-bold ${card.cyan ? "text-primary" : "text-foreground"}`}>{card.value}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground/50 mt-0.5">{card.label}</p>
                </motion.div>
              ))}
            </div>

            {/* Trust line */}
            <motion.div className="mt-2 md:mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5" initial={{ opacity: 0 }} animate={textVisible ? { opacity: 1 } : {}} transition={stagger(1.3)}>
              <span className="text-[10px] text-muted-foreground/40 tracking-widest uppercase">{t("hero.trustedBy")}</span>
              <div className="flex items-center gap-1.5">
                {clients.map((name) => (
                  <span key={name} className="text-[10px] text-foreground/50 font-medium rounded-full px-2.5 py-0.5" style={{ background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.08)" }}>{name}</span>
                ))}
              </div>
            </motion.div>

            {/* Scroll indicator — mobile */}
            <motion.div className="mt-2 flex justify-center md:hidden cursor-pointer" initial={{ opacity: 0 }} animate={textVisible ? { opacity: 0.3 } : {}} transition={stagger(1.3)} onClick={scrollToNext}>
              <ChevronDown className="w-5 h-5 text-foreground animate-bounce-arrow" />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll indicator — desktop */}
      <motion.div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[3] hidden md:block cursor-pointer" initial={{ opacity: 0 }} animate={textVisible ? { opacity: 0.3 } : {}} transition={stagger(1.3)} onClick={scrollToNext}>
        <ChevronDown className="w-5 h-5 text-foreground animate-bounce-arrow" />
      </motion.div>
    </section>
  );
};

export default ScrollHero;
